import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { financialAPI, buyersAPI, projectsAPI } from '@/lib/api-client'
import { formatDocument } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ChevronRight,
  Loader2,
  Scan,
  ArrowUpRight,
  ArrowDownRight,
  UserPlus,
} from 'lucide-react'

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('pt-BR')
}

const ENTITY_TYPE_OPTIONS = [
  { value: 'supplier', label: 'Fornecedor' },
  { value: 'buyer', label: 'Comprador' },
  { value: 'co-buyer', label: 'Co-comprador' },
  { value: 'investor', label: 'Investidor' },
  { value: 'contractor', label: 'Empreiteiro' },
  { value: 'broker', label: 'Corretor' },
  { value: 'collaborator', label: 'Colaborador' },
  { value: 'project_expense', label: 'Despesa de Obra' },
  { value: 'operational_expense', label: 'Despesa Operacional' },
]

// ==================== DetectedEntityCardList (left panel) ====================

interface DetectedEntityCardListProps {
  entities: any[]
  isLoading: boolean
  selectedDoc: string | null
  onSelect: (doc: string) => void
}

export function DetectedEntityCardList({
  entities,
  isLoading,
  selectedDoc,
  onSelect,
}: DetectedEntityCardListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    )
  }

  if (!entities || entities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-12">
        <Scan className="h-10 w-10 text-neutral-300" />
        <h3 className="mt-3 text-sm font-medium text-neutral-900">
          Nenhuma entidade detectada
        </h3>
        <p className="mt-1 text-xs text-neutral-500 text-center">
          Importe transações com nome e CPF/CNPJ na descrição para detectar automaticamente.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-2">
      {entities.map((entity: any) => {
        const isSelected = selectedDoc === entity.document

        return (
          <Card
            key={entity.document}
            className={`cursor-pointer transition-all ${
              isSelected
                ? 'ring-2 ring-primary shadow-md'
                : 'hover:shadow-sm'
            }`}
            onClick={() => onSelect(entity.document)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate text-neutral-900">
                      {entity.name}
                    </span>
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-300"
                    >
                      Não classificada
                    </Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-neutral-400 font-mono">
                    {formatDocument(entity.document)}
                  </p>
                  <div className="mt-1.5 flex items-center gap-4 text-xs">
                    {entity.totalIncome > 0 && (
                      <div className="flex items-center gap-1 text-green-600">
                        <ArrowUpRight className="h-3.5 w-3.5" />
                        <span className="font-medium">
                          {formatCurrency(entity.totalIncome)}
                        </span>
                      </div>
                    )}
                    {entity.totalExpense > 0 && (
                      <div className="flex items-center gap-1 text-red-600">
                        <ArrowDownRight className="h-3.5 w-3.5" />
                        <span className="font-medium">
                          {formatCurrency(entity.totalExpense)}
                        </span>
                      </div>
                    )}
                    <span className="text-neutral-400">
                      {entity.txCount} tx
                    </span>
                  </div>
                </div>
                {isSelected && (
                  <ChevronRight className="h-4 w-4 text-primary ml-2 mt-0.5" />
                )}
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

// ==================== DetectedEntityDetail (right panel / sheet) ====================

interface DetectedEntityDetailProps {
  entity: any
  onClassified: () => void
}

export function DetectedEntityDetail({ entity, onClassified }: DetectedEntityDetailProps) {
  const queryClient = useQueryClient()
  const [entityType, setEntityType] = useState<string>('')
  const [selectedBuyerId, setSelectedBuyerId] = useState<string>('')
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>('')

  const isExpenseType = entityType === 'project_expense' || entityType === 'operational_expense'
  const expenseCategoryType = entityType === 'project_expense' ? 'OBRA' : entityType === 'operational_expense' ? 'OPERACIONAL' : ''

  const { data: buyersData } = useQuery({
    queryKey: ['buyers', { page: 1, limit: 100 }],
    queryFn: () => buyersAPI.list({ page: 1, limit: 100 }),
    enabled: entityType === 'co-buyer',
  })
  const buyers = buyersData?.data || []

  const { data: projectsData } = useQuery({
    queryKey: ['projects', { limit: 100 }],
    queryFn: () => projectsAPI.list({ limit: 100 }),
    enabled: entityType === 'project_expense',
  })
  const projects = projectsData?.data || []

  const { data: expenseCategories } = useQuery({
    queryKey: ['expense-categories', expenseCategoryType],
    queryFn: () => financialAPI.listFinancialCategories(expenseCategoryType),
    enabled: isExpenseType,
  })

  const classifyMutation = useMutation({
    mutationFn: () =>
      financialAPI.classifyEntity({
        document: entity.document,
        name: entity.name,
        entityType,
        ...(entityType === 'co-buyer' && selectedBuyerId ? { buyerId: selectedBuyerId } : {}),
        ...(entityType === 'project_expense' && selectedProjectId ? { projectId: selectedProjectId } : {}),
        ...(isExpenseType && selectedCategoryName ? { categoryName: selectedCategoryName } : {}),
      }),
    onSuccess: (data: any) => {
      const typeLabel = ENTITY_TYPE_OPTIONS.find(o => o.value === entityType)?.label || entityType
      toast.success(
        `${entity.name} classificado como ${typeLabel}. ${data.matchedCount} transação(ões) vinculada(s).`
      )
      queryClient.invalidateQueries({ queryKey: ['reconciliation-detected-entities'] })
      queryClient.invalidateQueries({ queryKey: ['financial'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-entities-summary'] })
      onClassified()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base font-semibold text-neutral-900">
            {entity.name}
          </h3>
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-300"
          >
            Não classificada
          </Badge>
        </div>
        <p className="mt-1 text-sm text-neutral-500 font-mono">
          {formatDocument(entity.document)}
        </p>
        <div className="mt-2 flex items-center gap-6 text-sm">
          {entity.totalIncome > 0 && (
            <div className="flex items-center gap-1 text-green-600">
              <ArrowUpRight className="h-4 w-4" />
              <span className="font-medium">
                {formatCurrency(entity.totalIncome)}
              </span>
            </div>
          )}
          {entity.totalExpense > 0 && (
            <div className="flex items-center gap-1 text-red-600">
              <ArrowDownRight className="h-4 w-4" />
              <span className="font-medium">
                {formatCurrency(entity.totalExpense)}
              </span>
            </div>
          )}
          <span className="text-neutral-500">
            {entity.txCount} transação(ões)
          </span>
        </div>
      </div>

      {/* Classify action */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
        <p className="text-sm font-medium text-blue-800">
          Classificar esta entidade
        </p>
        <p className="text-xs text-blue-600">
          Ao classificar, a entidade será criada e todas as {entity.txCount} transações serão vinculadas automaticamente.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={entityType} onValueChange={(v) => { setEntityType(v); setSelectedBuyerId(''); setSelectedProjectId(''); setSelectedCategoryName('') }}>
            <SelectTrigger className="w-[220px] bg-white">
              <SelectValue placeholder="Selecione o tipo..." />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_TYPE_OPTIONS.map((opt, idx) => (
                idx === 6 ? (
                  <div key="separator">
                    <div className="my-1 mx-2 h-px bg-neutral-200" />
                    <SelectItem value={opt.value}>{opt.label}</SelectItem>
                  </div>
                ) : (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                )
              ))}
            </SelectContent>
          </Select>
          {entityType === 'co-buyer' && (
            <Select value={selectedBuyerId} onValueChange={setSelectedBuyerId}>
              <SelectTrigger className="w-[250px] bg-white">
                <SelectValue placeholder="Selecione o comprador..." />
              </SelectTrigger>
              <SelectContent>
                {buyers.map((b: any) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name} — {formatDocument(b.document)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {entityType === 'project_expense' && (
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger className="w-[220px] bg-white">
                <SelectValue placeholder="Selecione o projeto..." />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {isExpenseType && (
            <Select value={selectedCategoryName} onValueChange={setSelectedCategoryName}>
              <SelectTrigger className="w-[220px] bg-white">
                <SelectValue placeholder="Subcategoria..." />
              </SelectTrigger>
              <SelectContent>
                {(expenseCategories || []).map((c: any) => (
                  <SelectItem key={c.id} value={c.name}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={() => classifyMutation.mutate()}
            disabled={
              !entityType ||
              classifyMutation.isPending ||
              (entityType === 'co-buyer' && !selectedBuyerId) ||
              (entityType === 'project_expense' && (!selectedProjectId || !selectedCategoryName)) ||
              (entityType === 'operational_expense' && !selectedCategoryName)
            }
          >
            {classifyMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="mr-2 h-4 w-4" />
            )}
            Classificar e Vincular
          </Button>
        </div>
      </div>

      {/* Transactions table */}
      <div>
        <h4 className="mb-3 text-sm font-medium text-neutral-700">
          Transações
        </h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Valor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entity.transactions.map((tx: any) => (
              <TableRow key={tx.id}>
                <TableCell className="text-sm">
                  {formatDate(tx.date)}
                </TableCell>
                <TableCell className="text-sm max-w-[300px] truncate">
                  {tx.description}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      tx.type === 'INCOME'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }
                  >
                    {tx.type === 'INCOME' ? 'Entrada' : 'Saída'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right text-sm font-medium">
                  <span
                    className={
                      tx.type === 'INCOME'
                        ? 'text-green-600'
                        : 'text-red-600'
                    }
                  >
                    {tx.type === 'INCOME' ? '+' : '-'}
                    {formatCurrency(tx.amount)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
